import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import MenuChevronDownIcon from "@/assets/icons/tipo-menu-chevron-down.svg?react";
import PlusIcon from "@/assets/icons/tipo-plus.svg?react";
import TrashIcon from "@/assets/icons/tipo-trash.svg?react";
import type { TipoObraItem } from "../hooks/nova-obra.tipos";
import { Dropdown, FormField, Icon, Illustration } from "@/ds";
import {
  CARREGANDO,
  ETAPA_TIPOS_SUBTITLE,
  ETAPA_TIPOS_TITLE,
  LIMITES_CLASSE_VISIVEIS_ETAPAS,
  REQUIRED_FIELDS_NOTE,
  TIPOS_A11Y,
  TIPOS_LABELS,
  TIPOS_VAZIO,
} from "../nova-obra.constants";
import { AlterarDominanteDialog } from "./components/alterar-dominante-dialog";
import { formatDataBr } from "../components/format-data";
import { AvisoLimitesTipos } from "../components/aviso-condicoes";
import { InfoLimitesClasse } from "../components/info-limites-classe";
import { LimitesClasseDialog } from "../components/limites-classe-dialog";
import { TipoObraCard } from "./components/tipo-obra-card";
import { SEM_VALOR } from "./etapa-tipos-obra.constants";
import styles from "./etapa-tipos-obra.module.scss";
import type { EtapaTiposObraViewModel } from "./types-etapa-tipos-obra";

/** Botão que abre menu (Figma): "+" à esquerda, rótulo e chevron à direita. */
function ConteudoBotaoMenu({ label }: { readonly label: string }) {
  return (
    <>
      <PlusIcon aria-hidden />
      <span className="p-button-label">{label}</span>
      <MenuChevronDownIcon aria-hidden />
    </>
  );
}

/** Etapa 3 "Tipos de Obra" (HU 2525): CC principal, tipos por CC e CCs adicionais; estado no wizard. */
export function EtapaTiposObraView({
  errosVisiveis,
  dados,
  menuTipos,
  painelCentros,
  menuItens,
  abertos,
  busca,
  alterarBusca,
  limparBusca,
  verLimites,
  abrirLimites,
  fecharLimites,
  novoDominante,
  escolherNovoDominante,
  cancelarDominante,
  embarcacaoId,
  catalogo,
  centros,
  informacoes,
  periodos,
  duracao,
  cronograma,
  periodoObra,
  periodoWizard,
  alternar,
  atualizar,
  removerTipoDoCentro,
  removerCentroAdicional,
  abrirMenuTipos,
  escolherPrincipal,
  incluirCentro,
  naturezaPrincipal,
  opcoesPrincipal,
  disponiveisPrincipal,
  clientesDoTipo,
  embarcacaoNome,
  adicionaveis,
  grupos,
  errosInspecao,
  centrosAdicionais,
  uidDominante,
  tipoDominante,
  prioridadeTexto,
  confirmarDominante,
  todosAdicionados,
  erroPrincipal,
  semCentroCadastrado,
  mostraHintX,
}: EtapaTiposObraViewModel) {
  const renderCard = (
    tipo: TipoObraItem,
    centroUid: string | null,
    tiposDoCentro: readonly TipoObraItem[],
  ) => (
    <TipoObraCard
      key={tipo.uid}
      tipo={tipo}
      aberto={abertos.has(tipo.uid)}
      clientes={clientesDoTipo(tipo)}
      erroInspecao={errosInspecao[tipo.uid]}
      dominante={tipo.uid === uidDominante}
      prioridadeTexto={prioridadeTexto}
      onTornarDominante={() => escolherNovoDominante(tipo)}
      onToggle={() => alternar(tipo.uid)}
      onRemover={() => removerTipoDoCentro(tipo, centroUid, tiposDoCentro)}
      onChange={(campos) => atualizar(tipo.uid, campos)}
    />
  );

  return (
    <div className={styles.body} data-testid="etapa-tipos-obra">
      {novoDominante && tipoDominante && (
        <AlterarDominanteDialog
          atual={tipoDominante}
          novo={novoDominante}
          onConfirm={confirmarDominante}
          onCancel={cancelarDominante}
        />
      )}
      <Menu model={menuItens} popup ref={menuTipos} className={styles.menu} />

      <header className={styles.header}>
        <h2 className={styles.title}>{ETAPA_TIPOS_TITLE}</h2>
        <p className={styles.subtitle}>
          {ETAPA_TIPOS_SUBTITLE} {REQUIRED_FIELDS_NOTE}
        </p>
      </header>

      {(catalogo.status === "forbidden" ||
        catalogo.status === "error" ||
        centros.status === "forbidden" ||
        centros.status === "error") && (
        <div className={styles.alert} role="alert">
          {catalogo.errorMessage ?? centros.errorMessage}
        </div>
      )}

      {informacoes.status === "success" && informacoes.data && (
        <InfoLimitesClasse
          info={informacoes.data}
          dados={duracao}
          cronograma={cronograma}
          catalogo={periodos.data}
          onVerTodos={abrirLimites}
          maxTagsVisiveis={LIMITES_CLASSE_VISIVEIS_ETAPAS}
        />
      )}

      <AvisoLimitesTipos />
      {verLimites && embarcacaoId && (
        <LimitesClasseDialog
          embarcacaoId={embarcacaoId}
          periodo={periodoWizard}
          onClose={fecharLimites}
        />
      )}

      <section className={styles.contexto} data-testid="tipos-contexto">
        <div className={styles.dadosEmbarcacao}>
          {[
            [TIPOS_LABELS.embarcacao, embarcacaoNome],
            // Empresa e Unidade de Negócio: o bff ainda não os expõe (pendência 2 da HU 2525, `GET
            // .../informacoes` não os lista); mostram "—" até o contrato incluí-los.
            [TIPOS_LABELS.unidadeNegocio, SEM_VALOR],
            [TIPOS_LABELS.empresa, SEM_VALOR],
            [
              TIPOS_LABELS.periodoObra,
              periodoObra
                ? `${formatDataBr(periodoObra.inicio)} a ${formatDataBr(periodoObra.fim)}`
                : periodos.status === "loading"
                  ? CARREGANDO
                  : SEM_VALOR,
            ],
          ].map(([rotulo, valor]) => (
            <div key={rotulo} className={styles.leitura}>
              <span className={styles.leituraLabel}>{rotulo}</span>
              <span>{valor}</span>
            </div>
          ))}
        </div>

        <FormField.Root
          className={styles.field}
          error={erroPrincipal}
          help={
            !erroPrincipal && (mostraHintX || semCentroCadastrado)
              ? semCentroCadastrado
                ? TIPOS_LABELS.centroCustoSemCadastroHint
                : TIPOS_LABELS.centroCustoHint
              : undefined
          }
          disabled={semCentroCadastrado}
        >
          <FormField.Label>
            {dados.exigeCentroPrincipal
              ? TIPOS_LABELS.centroCustoObrigatorio
              : TIPOS_LABELS.centroCusto}
          </FormField.Label>
          <FormField.Control>
            <Dropdown
              value={dados.centroCustoPrincipalId}
              options={opcoesPrincipal}
              onChange={(event) => escolherPrincipal(event.value ?? null)}
              placeholder={
                semCentroCadastrado
                  ? TIPOS_LABELS.centroCustoSemCadastro
                  : TIPOS_LABELS.centroCustoPlaceholder
              }
              emptyMessage={TIPOS_LABELS.centroCustoSemOpcoes}
              filter
              filterPlaceholder=""
              size="sm"
              data-testid="tipos-centro-custo-select"
            />
          </FormField.Control>
          <FormField.Help />
          <FormField.Error />
        </FormField.Root>
      </section>

      <section className={styles.tipos}>
        <div className={styles.topbar}>
          <h3 className={styles.sectionTitle}>{TIPOS_LABELS.tiposTitle}</h3>
          <Button
            type="button"
            size="small"
            severity="secondary"
            disabled={disponiveisPrincipal.length === 0}
            className={styles.botaoMenu}
            onClick={(event) => abrirMenuTipos(event, null, naturezaPrincipal)}
            aria-haspopup="menu"
          >
            <ConteudoBotaoMenu label={TIPOS_LABELS.adicionarTipo} />
          </Button>
        </div>

        {dados.tipos.length === 0 ? (
          <div className={styles.vazio} data-testid="tipos-vazio">
            <p className={styles.vazioText}>{TIPOS_VAZIO}</p>
            <Illustration
              name="sem-equipe"
              size="natural"
              decorative
              testId="etapa-tipos-obra-ilustracao-vazio"
            />
            {errosVisiveis.tipos && (
              <small className={styles.error} role="alert">
                {errosVisiveis.tipos}
              </small>
            )}
          </div>
        ) : (
          <div className={styles.lista}>
            {dados.tipos.map((tipo) => renderCard(tipo, null, dados.tipos))}
          </div>
        )}
      </section>

      <section className={styles.outros} data-testid="centros-adicionais">
        <div className={styles.outrosHeader}>
          <div className={styles.outrosTexto}>
            <h3 className={styles.sectionTitle}>{TIPOS_LABELS.outrosCentrosTitle}</h3>
            <p className={styles.outrosDescricao}>{TIPOS_LABELS.outrosCentrosText}</p>
            {todosAdicionados && centros.data.length > 0 && (
              <p className={styles.outrosDescricao}>{TIPOS_LABELS.todosCentrosAdicionados}</p>
            )}
          </div>
          <Button
            type="button"
            size="small"
            severity="secondary"
            disabled={adicionaveis.length === 0}
            className={styles.botaoMenu}
            onClick={(event) => painelCentros.current?.toggle(event)}
            aria-haspopup="listbox"
          >
            <ConteudoBotaoMenu label={TIPOS_LABELS.adicionarCentro} />
          </Button>
          <OverlayPanel
            ref={painelCentros}
            className={styles.painel}
            pt={{ content: { className: styles.painelConteudo } }}
            onHide={limparBusca}
          >
            <div className={styles.painelBusca}>
              <InputText
                value={busca}
                onChange={(event) => alterarBusca(event.target.value)}
                aria-label={TIPOS_LABELS.buscarCentro}
                className={styles.input}
              />
              <Icon name="search" size={14} className={styles.lupa} />
            </div>
            <div className={styles.painelLista} role="listbox" data-testid="lista-centros-custo">
              {grupos.length === 0 && (
                <p className={styles.painelVazio}>{TIPOS_LABELS.semResultadoCentro}</p>
              )}
              {grupos.map((grupo) => (
                <div key={grupo.titulo} role="group" aria-label={grupo.titulo}>
                  <p className={styles.grupoTitulo}>{grupo.titulo}</p>
                  {grupo.itens.map(({ centro, rotulo }) => (
                    <button
                      key={centro.id}
                      type="button"
                      role="option"
                      aria-selected={false}
                      className={styles.painelItem}
                      onClick={() => incluirCentro(centro)}
                    >
                      {rotulo}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </OverlayPanel>
        </div>

        {centrosAdicionais.map(
          ({
            adicional,
            testId,
            natureza,
            tag,
            rotulo,
            aceita,
            semTiposDisponiveis,
            codigoUnico,
            nomeUnico,
          }) => {
            return (
              <div key={adicional.uid} className={styles.adicional} data-testid={testId}>
                <div className={styles.adicionalHeader}>
                  <span className={styles.tagCentro}>{tag}</span>
                  <span className={styles.adicionalNome}>{rotulo}</span>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`${TIPOS_A11Y.removerCentro} ${adicional.centro.centroCusto}`}
                    onClick={() => removerCentroAdicional(adicional)}
                  >
                    <TrashIcon aria-hidden />
                  </button>
                </div>
                {aceita && <p className={styles.aceita}>{aceita}</p>}

                {adicional.tipos.length === 0 ? (
                  <div className={styles.escopoVazio} data-testid="centro-sem-tipo">
                    <div className={styles.escopoVazioTexto}>
                      <p className={styles.escopoTitle}>{TIPOS_LABELS.semTipoDoCentro}</p>
                      {codigoUnico && (
                        <p className={styles.escopoText}>
                          {TIPOS_LABELS.semTipoDoCentroAceita(codigoUnico, nomeUnico)}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="small"
                      severity="secondary"
                      icon={<PlusIcon aria-hidden />}
                      label={TIPOS_LABELS.adicionarTipo}
                      disabled={semTiposDisponiveis}
                      onClick={(event) => abrirMenuTipos(event, adicional.uid, natureza)}
                    />
                  </div>
                ) : (
                  <>
                    <div className={styles.lista}>
                      {adicional.tipos.map((tipo) =>
                        renderCard(tipo, adicional.uid, adicional.tipos),
                      )}
                    </div>
                    {!semTiposDisponiveis && (
                      <Button
                        type="button"
                        size="small"
                        text
                        icon={<PlusIcon aria-hidden />}
                        label={TIPOS_LABELS.adicionarTipo}
                        onClick={(event) => abrirMenuTipos(event, adicional.uid, natureza)}
                        className={styles.adicionarNoCentro}
                      />
                    )}
                  </>
                )}
              </div>
            );
          },
        )}
      </section>
    </div>
  );
}
